import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { ArrowLeft, LogOut, RefreshCw, Trash2 } from "lucide-react";
import { BRAND } from "../../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "invogue_admin_token";

const STATUSES = [
  { id: "nouvelle", label: "Nouvelle" },
  { id: "en_cours", label: "En cours" },
  { id: "traitee", label: "Traitée" },
];
const statusLabel = (s) => STATUSES.find((x) => x.id === s)?.label || s;
const URGENCY = { standard: "Standard", prioritaire: "Prioritaire", urgent: "Urgent" };

const formatError = (detail) => {
  if (detail == null) return "Une erreur est survenue.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  return String(detail);
};

const inputCls =
  "w-full rounded-[2px] border border-line-light bg-surface px-4 py-3.5 font-sans text-sm font-light text-ivory placeholder:text-neutral-600 focus:border-champagne/60 focus:outline-none transition-colors duration-300";

const StatusBadge = ({ status }) => (
  <span
    className={`rounded-[2px] border px-2.5 py-1 font-display text-[9px] font-semibold uppercase tracking-[0.16em] ${
      status === "nouvelle"
        ? "border-champagne/60 bg-champagne/10 text-champagne"
        : status === "en_cours"
          ? "border-ivory/40 text-ivory"
          : "border-line-light text-neutral-500"
    }`}
    data-testid={`status-badge-${status}`}
  >
    {statusLabel(status)}
  </span>
);

const NoteEditor = ({ request, onSave, testid }) => {
  const [value, setValue] = useState(request.notes || "");
  const [saved, setSaved] = useState(false);
  const dirty = value !== (request.notes || "");
  return (
    <div className="mt-6 border-t border-line pt-5" data-testid={`${testid}-block`}>
      <span className="mb-2 block font-display text-[9px] font-medium uppercase tracking-[0.18em] text-muted">
        Note interne — visible uniquement ici
      </span>
      <textarea
        value={value}
        onChange={(e) => {
          setValue(e.target.value);
          setSaved(false);
        }}
        rows={2}
        placeholder="Devis reçu, prestataire contacté, avancement…"
        className={`${inputCls} resize-none`}
        data-testid={testid}
      />
      <div className="mt-2.5 flex items-center gap-4">
        <button
          onClick={async () => {
            await onSave(request.id, value);
            setSaved(true);
          }}
          disabled={!dirty}
          className="rounded-[2px] border border-champagne/50 px-3.5 py-2 font-display text-[9px] font-semibold uppercase tracking-[0.14em] text-champagne transition-colors duration-300 hover:bg-champagne hover:text-ink disabled:cursor-default disabled:opacity-40"
          data-testid={`${testid}-save`}
        >
          Enregistrer la note
        </button>
        {saved && !dirty && (
          <span className="font-sans text-[11px] font-light text-champagne/80" data-testid={`${testid}-saved`}>
            Note enregistrée.
          </span>
        )}
      </div>
    </div>
  );
};

const Admin = () => {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [authChecked, setAuthChecked] = useState(false);
  const [requests, setRequests] = useState(null);
  const [filter, setFilter] = useState("all");
  const [error, setError] = useState("");
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [loginState, setLoginState] = useState("idle");

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setRequests(null);
    setAuthChecked(true);
  }, []);

  const loadRequests = useCallback(
    async (t) => {
      try {
        const { data } = await axios.get(`${API}/requests`, {
          headers: { Authorization: `Bearer ${t}` },
        });
        setRequests(data);
        setAuthChecked(true);
      } catch (e) {
        if (e.response?.status === 401) {
          logout();
        } else {
          setError(formatError(e.response?.data?.detail));
          setAuthChecked(true);
        }
      }
    },
    [logout]
  );

  useEffect(() => {
    if (token) loadRequests(token);
    else setAuthChecked(true);
  }, [token, loadRequests]);

  const submitLogin = async (e) => {
    e.preventDefault();
    setLoginState("sending");
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, loginForm);
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
    } catch (err) {
      setError(formatError(err.response?.data?.detail));
      setLoginState("idle");
    }
  };

  const updateRequest = async (id, payload) => {
    try {
      const { data } = await axios.patch(`${API}/requests/${id}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRequests((rs) => rs.map((r) => (r.id === id ? data : r)));
    } catch (e) {
      if (e.response?.status === 401) logout();
    }
  };

  const setStatus = (id, status) => updateRequest(id, { status });
  const saveNote = (id, notes) => updateRequest(id, { notes });

  const remove = async (id) => {
    if (!window.confirm("Supprimer définitivement cette demande ?")) return;
    try {
      await axios.delete(`${API}/requests/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRequests((rs) => rs.filter((r) => r.id !== id));
    } catch (e) {
      if (e.response?.status === 401) logout();
    }
  };

  if (!authChecked) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink" data-testid="admin-loading">
        <p className="font-display text-[11px] tracking-[0.24em] text-muted uppercase">Chargement…</p>
      </div>
    );
  }

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink px-6" data-testid="admin-login">
        <div className="w-full max-w-[420px]">
          <p className="font-display text-[18px] font-semibold tracking-[0.08em] text-ivory">{BRAND.name}</p>
          <p className="mt-1.5 font-sans text-[9px] uppercase tracking-[0.16em] text-muted">{BRAND.tag}</p>
          <h1 className="mt-10 font-serif text-4xl font-medium text-ivory">Bureau privé.</h1>
          <p className="mt-3 font-sans text-sm font-light text-neutral-500">
            Accès réservé — gestion des demandes clients.
          </p>
          <form onSubmit={submitLogin} className="mt-8 space-y-5" data-testid="admin-login-form">
            <input
              type="email"
              required
              placeholder="Email"
              value={loginForm.email}
              onChange={(e) => setLoginForm((f) => ({ ...f, email: e.target.value }))}
              className={inputCls}
              data-testid="admin-email-input"
            />
            <input
              type="password"
              required
              placeholder="Mot de passe"
              value={loginForm.password}
              onChange={(e) => setLoginForm((f) => ({ ...f, password: e.target.value }))}
              className={inputCls}
              data-testid="admin-password-input"
            />
            {error && (
              <p className="font-sans text-[13px] text-red-400/90" data-testid="admin-login-error">
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={loginState === "sending"}
              className="w-full rounded-[2px] bg-champagne px-[22px] py-[14px] font-display text-[11px] font-semibold uppercase tracking-[0.14em] text-[#0a0a0a] transition-colors duration-300 hover:bg-ivory disabled:opacity-50"
              data-testid="admin-login-submit"
            >
              {loginState === "sending" ? "Connexion…" : "Se connecter"}
            </button>
          </form>
          <a
            href="/"
            className="mt-8 inline-flex items-center gap-2 font-display text-[10px] font-medium uppercase tracking-[0.16em] text-muted transition-colors hover:text-ivory"
            data-testid="admin-back-to-site"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Retour au site
          </a>
        </div>
      </div>
    );
  }

  const list = requests || [];
  const counts = {
    all: list.length,
    nouvelle: list.filter((r) => r.status === "nouvelle").length,
    en_cours: list.filter((r) => r.status === "en_cours").length,
    traitee: list.filter((r) => r.status === "traitee").length,
  };
  const visible = filter === "all" ? list : list.filter((r) => r.status === filter);

  return (
    <div className="min-h-screen bg-ink text-ivory" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 border-b border-white/[0.08] bg-[#0a0a0a]/[0.92] backdrop-blur-[16px]">
        <div className="mx-auto flex h-[72px] max-w-[1200px] items-center justify-between px-6">
          <div className="flex items-baseline gap-4">
            <span className="font-display text-[16px] font-semibold tracking-[0.08em]">{BRAND.name}</span>
            <span className="hidden font-display text-[10px] uppercase tracking-[0.2em] text-champagne sm:block">
              Bureau privé
            </span>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => loadRequests(token)}
              className="inline-flex items-center gap-2 rounded-[2px] border border-line-light px-4 py-2.5 font-display text-[10px] font-semibold uppercase tracking-[0.14em] text-neutral-400 transition-colors hover:border-champagne/60 hover:text-ivory"
              data-testid="admin-refresh-button"
            >
              <RefreshCw className="h-3.5 w-3.5" /> Actualiser
            </button>
            <button
              onClick={logout}
              className="inline-flex items-center gap-2 rounded-[2px] border border-line-light px-4 py-2.5 font-display text-[10px] font-semibold uppercase tracking-[0.14em] text-neutral-400 transition-colors hover:border-red-400/50 hover:text-red-300"
              data-testid="admin-logout-button"
            >
              <LogOut className="h-3.5 w-3.5" /> Quitter
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1200px] px-6 py-12">
        <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <div>
            <h1 className="font-serif text-4xl font-medium md:text-5xl">Demandes clients.</h1>
            <p className="mt-3 font-sans text-sm font-light text-neutral-500" data-testid="admin-stats">
              {counts.all} demande{counts.all > 1 ? "s" : ""} — {counts.nouvelle} nouvelle
              {counts.nouvelle > 1 ? "s" : ""}, {counts.en_cours} en cours, {counts.traitee} traitée
              {counts.traitee > 1 ? "s" : ""}
            </p>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="admin-filters">
            {[{ id: "all", label: "Toutes" }, ...STATUSES].map((f) => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                className={`rounded-[2px] border px-4 py-2.5 font-display text-[10px] font-semibold uppercase tracking-[0.14em] transition-colors duration-300 ${
                  filter === f.id
                    ? "border-champagne/70 bg-champagne/10 text-champagne"
                    : "border-line-light text-neutral-500 hover:text-neutral-300"
                }`}
                data-testid={`admin-filter-${f.id}`}
              >
                {f.label} · {f.id === "all" ? counts.all : counts[f.id]}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-10 space-y-4">
          {visible.length === 0 && (
            <div className="border border-line-light p-12 text-center" data-testid="admin-empty-state">
              <p className="font-serif text-2xl italic text-neutral-500">Aucune demande pour le moment.</p>
            </div>
          )}
          {visible.map((r, i) => (
            <article key={r.id} className="border border-line-light bg-surface p-6 md:p-8" data-testid={`request-card-${i}`}>
              <div className="flex flex-wrap items-center gap-3">
                <StatusBadge status={r.status} />
                <span
                  className={`rounded-[2px] border px-2.5 py-1 font-display text-[9px] font-semibold uppercase tracking-[0.16em] ${
                    r.urgency === "urgent"
                      ? "border-red-400/50 text-red-300"
                      : r.urgency === "prioritaire"
                        ? "border-ivory/30 text-ivory/80"
                        : "border-line-light text-neutral-500"
                  }`}
                  data-testid={`request-urgency-${i}`}
                >
                  {URGENCY[r.urgency] || r.urgency}
                </span>
                <span className="ml-auto font-sans text-[11px] font-light text-neutral-600">
                  {new Date(r.created_at).toLocaleDateString("fr-FR", {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </span>
              </div>

              <p className="mt-5 max-w-[720px] font-serif text-xl font-medium leading-[1.5] text-ivory md:text-[22px]">
                {r.message}
              </p>

              <dl className="mt-6 grid grid-cols-2 gap-x-8 gap-y-3 border-t border-line pt-5 md:grid-cols-3">
                {[
                  ["Nom", r.name],
                  ["Email", r.email || r.contact],
                  ["Téléphone", r.phone],
                  ["Date souhaitée", r.date],
                  ["Lieu", r.location],
                  ["Personnes", r.guests],
                  ["Budget", r.budget],
                ]
                  .filter(([, v]) => v)
                  .map(([k, v]) => (
                    <div key={k}>
                      <dt className="font-display text-[9px] font-medium uppercase tracking-[0.18em] text-muted">{k}</dt>
                      <dd className="mt-1 font-sans text-[13px] font-light text-neutral-300">{v}</dd>
                    </div>
                  ))}
              </dl>

              <div className="mt-6 flex flex-wrap items-center gap-2 border-t border-line pt-5">
                {STATUSES.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => setStatus(r.id, s.id)}
                    disabled={r.status === s.id}
                    className={`rounded-[2px] border px-3.5 py-2 font-display text-[9px] font-semibold uppercase tracking-[0.14em] transition-colors duration-300 ${
                      r.status === s.id
                        ? "cursor-default border-champagne/60 bg-champagne/10 text-champagne"
                        : "border-line-light text-neutral-500 hover:border-champagne/50 hover:text-ivory"
                    }`}
                    data-testid={`request-status-${s.id}-${i}`}
                  >
                    {s.label}
                  </button>
                ))}
                <button
                  onClick={() => remove(r.id)}
                  className="ml-auto inline-flex items-center gap-2 rounded-[2px] border border-line-light px-3.5 py-2 font-display text-[9px] font-semibold uppercase tracking-[0.14em] text-neutral-600 transition-colors hover:border-red-400/50 hover:text-red-300"
                  data-testid={`request-delete-${i}`}
                >
                  <Trash2 className="h-3 w-3" /> Supprimer
                </button>
              </div>

              <NoteEditor request={r} onSave={saveNote} testid={`request-note-${i}`} />
            </article>
          ))}
        </div>
      </main>
    </div>
  );
};

export default Admin;
